import {
  DEFAULT_MODEL_ENABLED_STATE,
  FREE_MODELS,
} from "@/lib/constants/app";

/**
 * Get the list of enabled models based on user authentication and preferences
 * This function provides a single source of truth for model filtering across the application
 *
 * @param {Object|null} user - The authenticated user object (null if not logged in)
 * @param {Object} userModelPreferences - User's model preference settings (from profile.settings.models)
 * @returns {Array<string>} - Array of enabled model IDs
 *
 * @example
 * // For unauthenticated users
 * const models = getEnabledModels(null, {});
 * // Returns: FREE_MODELS array
 *
 * @example
 * // For authenticated user with no preferences set
 * const models = getEnabledModels(user, {});
 * // Returns: All models from FREE_MODELS + DEFAULT_MODEL_ENABLED_STATE (excluding those set to false)
 *
 * @example
 * // For authenticated user with preferences
 * const models = getEnabledModels(user, { "gpt-4o": true, "gpt-4o-mini": false });
 * // Returns: Models based on user preferences with DEFAULT_MODEL_ENABLED_STATE as fallback
 */
export function getEnabledModels(user, userModelPreferences = {}) {
  // Normalize preferences to handle null/undefined
  const preferences = userModelPreferences || {};

  // Get all available models (FREE_MODELS + models in DEFAULT_MODEL_ENABLED_STATE)
  const allAvailableModels = new Set([
    ...FREE_MODELS,
    ...Object.keys(DEFAULT_MODEL_ENABLED_STATE),
  ]);

  // If user is not logged in, show all free models only
  if (!user) {
    return FREE_MODELS;
  }

  // If no preferences are set, show all models that are enabled by default
  if (Object.keys(preferences).length === 0) {
    return Array.from(allAvailableModels).filter(
      (modelId) => DEFAULT_MODEL_ENABLED_STATE[modelId] !== false
    );
  }

  // Filter models based on user preferences
  return Array.from(allAvailableModels).filter((modelId) => {
    // If the model is explicitly set to true in user preferences, include it
    if (preferences[modelId] === true) return true;
    // If the model is explicitly set to false in user preferences, exclude it
    if (preferences[modelId] === false) return false;
    // If not in user preferences, use the default state
    return DEFAULT_MODEL_ENABLED_STATE[modelId] !== false;
  });
}

/**
 * Sort models with pinned models appearing first
 * Pinned models maintain their pinned order, unpinned models maintain their original order
 *
 * @param {Array<Object>} models - Array of model option objects with { value, label, ... }
 * @param {Array<string>} pinnedModelIds - Array of pinned model IDs in pinned order
 * @returns {Array<Object>} - Sorted array with pinned models first
 *
 * @example
 * const sorted = sortModelsWithPinnedFirst(modelOptions, ["gpt-5", "claude-sonnet-4"]);
 */
export function sortModelsWithPinnedFirst(models, pinnedModelIds = []) {
  if (!pinnedModelIds || pinnedModelIds.length === 0) {
    return models;
  }

  return [...models].sort((a, b) => {
    const aIsPinned = pinnedModelIds.includes(a.value);
    const bIsPinned = pinnedModelIds.includes(b.value);

    // Pinned models come first
    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    // Within pinned models, maintain the order they were pinned
    if (aIsPinned && bIsPinned) {
      return pinnedModelIds.indexOf(a.value) - pinnedModelIds.indexOf(b.value);
    }

    // Non-pinned models maintain their original order
    return 0;
  });
}
